import {
    PILLAR_ROTATION,
    buildIdeaPool,
    type Niche,
    type PillarId,
} from '@/data/contentIdeas'

export interface CalendarSlot {
    date: Date
    platform: string
    pillar: PillarId
    idea: string
}

export interface GenerateOptions {
    niche: Niche
    platforms: string[]
    postsPerWeek: number
    startDate: Date
    weeks: number
    /** Changing the seed reshuffles which idea lands on which day. */
    seed: number
    /**
     * Resolves an idea's translation key to copy in the active language.
     * The pool carries keys, not text, so only the caller — which has t() —
     * can turn one into a sentence. Same shape as the pillarLabel resolver
     * toCSV and toICS already take.
     */
    translateIdea: (textKey: string) => string
}

/** Small deterministic PRNG so "acak ulang" is reproducible from a seed. */
function mulberry32(seed: number): () => number {
    let a = seed >>> 0
    return () => {
        a = (a + 0x6d2b79f5) >>> 0
        let t = Math.imul(a ^ (a >>> 15), 1 | a)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

function shuffle<T>(items: T[], random: () => number): T[] {
    const copy = [...items]
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1))
            ;[copy[i], copy[j]] = [copy[j]!, copy[i]!]
    }
    return copy
}

/**
 * Spreads `postsPerWeek` posts across a 7-day week as evenly as possible, so 3
 * posts land roughly Mon/Wed/Fri rather than three days in a row.
 */
function dayOffsetsForWeek(postsPerWeek: number): number[] {
    return Array.from({ length: postsPerWeek }, (_, i) =>
        Math.round((i * 7) / postsPerWeek)
    )
}

export function generateCalendar({
    niche,
    platforms,
    postsPerWeek,
    startDate,
    weeks,
    seed,
    translateIdea,
}: GenerateOptions): CalendarSlot[] {
    const random = mulberry32(seed)
    const pool = buildIdeaPool(niche)

    // One shuffled queue per pillar, consumed in order so a pillar cycles through
    // every idea it has before repeating any of them.
    const queues = new Map<PillarId, string[]>()
    const cursors = new Map<PillarId, number>()
    for (const { pillar } of pool) {
        if (queues.has(pillar)) continue
        const ideas = pool.filter((idea) => idea.pillar === pillar).map((idea) => translateIdea(idea.textKey))
        queues.set(pillar, shuffle(ideas, random))
        cursors.set(pillar, 0)
    }

    const nextIdea = (pillar: PillarId): string => {
        const queue = queues.get(pillar)
        if (!queue || queue.length === 0) return translateIdea('contentIdeas.fallback')
        const cursor = cursors.get(pillar) ?? 0
        cursors.set(pillar, cursor + 1)
        return queue[cursor % queue.length]!
    }

    const offsets = dayOffsetsForWeek(postsPerWeek)
    const slots: CalendarSlot[] = []
    let index = 0

    for (let week = 0; week < weeks; week++) {
        for (const offset of offsets) {
            const date = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate() + week * 7 + offset
            )
            const pillar = PILLAR_ROTATION[index % PILLAR_ROTATION.length]!
            slots.push({
                date,
                platform: platforms[index % platforms.length] ?? 'Instagram',
                pillar,
                idea: nextIdea(pillar),
            })
            index++
        }
    }

    return slots
}

function pad(value: number): string {
    return String(value).padStart(2, '0')
}

/** YYYY-MM-DD from local date parts — avoids the UTC shift toISOString() would cause. */
export function toISODate(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Formats a slot date in the active language rather than always Indonesian. */
export function formatDate(date: Date, locale: string): string {
    return date.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    })
}

function escapeCsv(value: string): string {
    // Leading =, +, -, @ are treated as formulas by Excel/Sheets; prefix with a
    // quote so a pasted idea can never execute as one.
    const guarded = /^[=+\-@]/.test(value) ? `'${value}` : value
    return `"${guarded.replace(/"/g, '""')}"`
}

export function toCSV(slots: CalendarSlot[], pillarLabel: (id: PillarId) => string): string {
    const header = ['Tanggal', 'Hari', 'Platform', 'Pilar Konten', 'Ide Konten']
    const rows = slots.map((slot) => [
        toISODate(slot.date),
        slot.date.toLocaleDateString('id-ID', { weekday: 'long' }),
        slot.platform,
        pillarLabel(slot.pillar),
        slot.idea,
    ])
    const body = [header, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\r\n')
    // BOM so Excel opens the Indonesian characters as UTF-8 instead of mojibake.
    return `\uFEFF${body}`
}

function icsEscape(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
}

function icsDate(date: Date): string {
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`
}

/**
 * RFC 5545 caps a content line at 75 octets; longer lines must be split with a
 * CRLF followed by a single space. Counted in UTF-8 bytes, not characters, so a
 * caption full of accents or emoji still folds at a legal boundary.
 */
function foldLine(line: string): string {
    const encoder = new TextEncoder()
    if (encoder.encode(line).length <= 75) return line

    const parts: string[] = []
    let current = ''
    let currentBytes = 0
    // Iterate by code point so a multi-byte character is never split in half.
    for (const char of line) {
        const charBytes = encoder.encode(char).length
        // Continuation lines carry a leading space, so their budget is 74.
        const budget = parts.length === 0 ? 75 : 74
        if (currentBytes + charBytes > budget) {
            parts.push(current)
            current = ''
            currentBytes = 0
        }
        current += char
        currentBytes += charBytes
    }
    if (current) parts.push(current)

    return parts.join('\r\n ')
}

export function toICS(slots: CalendarSlot[], pillarLabel: (id: PillarId) => string): string {
    const stamp = new Date()
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '')

    const events = slots.flatMap((slot, index) => {
        const end = new Date(
            slot.date.getFullYear(),
            slot.date.getMonth(),
            slot.date.getDate() + 1
        )
        return [
            'BEGIN:VEVENT',
            `UID:planwise-${icsDate(slot.date)}-${index}@planwise.social`,
            `DTSTAMP:${stamp}`,
            // All-day events: the tool does not claim to know your best posting hour.
            `DTSTART;VALUE=DATE:${icsDate(slot.date)}`,
            `DTEND;VALUE=DATE:${icsDate(end)}`,
            `SUMMARY:${icsEscape(`[${slot.platform}] ${slot.idea}`)}`,
            `DESCRIPTION:${icsEscape(`Pilar konten: ${pillarLabel(slot.pillar)}\nDibuat dengan PlanWise — https://www.planwise.social/tools/content-calendar-generator`)}`,
            'END:VEVENT',
        ]
    })

    return [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//PlanWise//Content Calendar Generator//ID',
        'CALSCALE:GREGORIAN',
        ...events,
        'END:VCALENDAR',
    ]
        .map(foldLine)
        .join('\r\n')
}

export function downloadFile(content: string, filename: string, mimeType: string) {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}
